import { Box, SxProps, Theme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { CommonIcon, CommonIconProps } from './CommonIcon';

type AnimationConfig = {
  keyframes: Record<string, React.CSSProperties>;
  animation: string;
};

const iconAnimations: Record<string, AnimationConfig> = {
  favorite: {
    keyframes: {
      '0%': { transform: 'scale(1)' },
      '35%': { transform: 'scale(1.22)' },
      '55%': { transform: 'scale(0.95)' },
      '100%': { transform: 'scale(1)' },
    },
    animation: 'iconAnimation 0.45s ease-out 1',
  },
  balance: {
    keyframes: {
      '0%': { transform: 'rotate(0deg) scale(1)' },
      '30%': { transform: 'rotate(-12deg) scale(1.12)' },
      '60%': { transform: 'rotate(12deg) scale(1.12)' },
      '100%': { transform: 'rotate(0deg) scale(1)' },
    },
    animation: 'iconAnimation 0.5s ease-in-out 1',
  },
  block: {
    keyframes: {
      '0%': { transform: 'scale(1)' },
      '20%': { transform: 'scale(1.15) translateX(-2px)' },
      '40%': { transform: 'scale(1.15) translateX(2px)' },
      '60%': { transform: 'scale(1.15) translateX(-1px)' },
      '100%': { transform: 'scale(1)' },
    },
    animation: 'iconAnimation 0.45s ease-out 1',
  },
  cake: {
    keyframes: {
      '0%': { transform: 'translateY(0) scale(1)' },
      '40%': { transform: 'translateY(-5px) scale(1.18)' },
      '70%': { transform: 'translateY(2px) scale(0.98)' },
      '100%': { transform: 'translateY(0) scale(1)' },
    },
    animation: 'iconAnimation 0.5s ease-out 1',
  },
  explore: {
    keyframes: {
      '0%': { transform: 'rotate(0deg) scale(1)' },
      '50%': { transform: 'rotate(90deg) scale(1.3)' },
      '100%': { transform: 'rotate(180deg) scale(1)' },
    },
    animation: 'iconAnimation 0.5s ease-out 1',
  },
  emoji_events: {
    keyframes: {
      '0%': { transform: 'rotate(0deg) scale(1)' },
      '25%': { transform: 'rotate(-15deg) scale(1.2)' },
      '50%': { transform: 'rotate(15deg) scale(1.3)' },
      '75%': { transform: 'rotate(-8deg) scale(1.15)' },
      '100%': { transform: 'rotate(0deg) scale(1)' },
    },
    animation: 'iconAnimation 0.5s ease-in-out 1',
  },
  category: {
    keyframes: {
      '0%': { transform: 'translateY(0) scale(1)' },
      '50%': { transform: 'translateY(-4px) scale(1.3)' },
      '100%': { transform: 'translateY(0) scale(1)' },
    },
    animation: 'iconAnimation 0.5s ease-out 1',
  },
  dashboard: {
    keyframes: {
      '0%': { transform: 'scale(0.88)' },
      '45%': { transform: 'scale(1.2)' },
      '70%': { transform: 'scale(0.97)' },
      '100%': { transform: 'scale(1)' },
    },
    animation: 'iconAnimation 0.45s ease-out 1',
  },
  settings: {
    keyframes: {
      '0%': { transform: 'rotate(0deg) scale(1)' },
      '50%': { transform: 'rotate(180deg) scale(1.3)' },
      '100%': { transform: 'rotate(360deg) scale(1)' },
    },
    animation: 'iconAnimation 0.6s ease-out 1',
  },
  monitor_weight: {
    keyframes: {
      '0%': { transform: 'scale(1)' },
      '35%': { transform: 'scale(1.22)' },
      '55%': { transform: 'scale(0.95)' },
      '100%': { transform: 'scale(1)' },
    },
    animation: 'iconAnimation 0.45s ease-out 1',
  },
  menu_book: {
    keyframes: {
      '0%': { transform: 'rotate(0deg) scale(1)' },
      '25%': { transform: 'rotate(-10deg) scale(1.15)' },
      '50%': { transform: 'rotate(10deg) scale(1.25)' },
      '75%': { transform: 'rotate(-5deg) scale(1.1)' },
      '100%': { transform: 'rotate(0deg) scale(1)' },
    },
    animation: 'iconAnimation 0.5s ease-in-out 1',
  },
  local_fire_department: {
    keyframes: {
      '0%': { transform: 'scale(1)' },
      '35%': { transform: 'scale(1.22)' },
      '55%': { transform: 'scale(0.95)' },
      '100%': { transform: 'scale(1)' },
    },
    animation: 'iconAnimation 0.45s ease-out 1',
  },
  shield: {
    keyframes: {
      '0%': { transform: 'scale(1)' },
      '35%': { transform: 'scale(1.22)' },
      '55%': { transform: 'scale(0.95)' },
      '100%': { transform: 'scale(1)' },
    },
    animation: 'iconAnimation 0.45s ease-out 1',
  },
  directions_walk: {
    keyframes: {
      '0%': { transform: 'scale(1)' },
      '35%': { transform: 'scale(1.22)' },
      '55%': { transform: 'scale(0.95)' },
      '100%': { transform: 'scale(1)' },
    },
    animation: 'iconAnimation 0.45s ease-out 1',
  },
};

export type AnimatedCommonIconProps = CommonIconProps & {
  isSelected: boolean;
};

export const AnimatedCommonIcon = ({ isSelected, icon, ...iconProps }: AnimatedCommonIconProps) => {
  const prevSelectedRef = useRef<boolean | null>(null);
  const [animationKey, setAnimationKey] = useState(0);
  const animationConfig = iconAnimations[icon];

  useEffect(() => {
    if (isSelected && prevSelectedRef.current === false && animationConfig) {
      setAnimationKey(prev => prev + 1);
    }
    prevSelectedRef.current = isSelected;
  }, [isSelected, animationConfig]);

  if (!animationConfig) {
    return <CommonIcon icon={icon} {...iconProps} />;
  }

  const animationName = `iconAnimation-${icon}`;
  const sx: SxProps<Theme> = {
    display: 'inline-flex',
    [`@keyframes ${animationName}`]: animationConfig.keyframes,
    '& .material-icons-outlined': isSelected ? {
      animation: animationConfig.animation.replace('iconAnimation', animationName),
      transformOrigin: 'center',
    } : {
      transformOrigin: 'center',
    },
  };

  return (
    <Box
      key={`animated-icon-${icon}-${animationKey}`}
      sx={sx}
    >
      <CommonIcon icon={icon} {...iconProps} />
    </Box>
  );
};
